import { Banner, Box, FlexBox, Text, Tooltip } from '@dixa/lib-shared-habla';

import styles from './sidebarCardsSummary.module.scss';

type SidebarCardsSummaryProps = {
  /** How many cards are hidden for any reason. */
  readonly hiddenCount: number;
  /** How many of the hidden cards are hidden by their conditions. */
  readonly conditionHiddenCount: number;
  /** Whether the hidden cards are currently force-shown. */
  readonly isForceShown: boolean;
  /** Toggles the force-show state. */
  readonly onToggle: () => void;
};

/**
 * Summary of how many sidebar cards are hidden, with an inline show/hide link
 * that temporarily reveals them. Mirrors the native integrations'
 * conditional-render summary (info banner + filter icon). Renders nothing when
 * no card is hidden; the tooltip breaks the hidden count into always-hidden vs.
 * conditionally-hidden.
 */
export const SidebarCardsSummary = ({
  hiddenCount,
  conditionHiddenCount,
  isForceShown,
  onToggle,
}: SidebarCardsSummaryProps): JSX.Element | null => {
  if (hiddenCount === 0) {
    return null;
  }

  const alwaysHiddenCount = hiddenCount - conditionHiddenCount;

  return (
    <Box padding="200 300">
      <Tooltip
        content={
          <FlexBox flexDirection="column" gap="100">
            <Text.Paragraph variant="4" color="neutral.000">
              {alwaysHiddenCount} always hidden
            </Text.Paragraph>
            <Text.Paragraph variant="4" color="neutral.000">
              {conditionHiddenCount} conditionally hidden
            </Text.Paragraph>
          </FlexBox>
        }
      >
        <Banner
          variant="info"
          icon="filter"
          description={
            <Text.Paragraph variant="4" subdued>
              {hiddenCount} hidden{' '}
              <button
                type="button"
                className={styles.toggleLink}
                onClick={onToggle}
              >
                ({isForceShown ? 'hide' : 'show'})
              </button>
            </Text.Paragraph>
          }
        />
      </Tooltip>
    </Box>
  );
};
